import type { Attachment, Event, ScheduleCard } from '@pa/core';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { api, readLastSeen, takePrefetchedHistory, writeLastSeen, type HistoryPage } from './api';
import { useSession } from './session';
import {
  useAgentSocket,
  type AgentTurnState,
  type ApprovalFrame,
  type ConnectionActionFrame,
  type ScheduleFrame,
  type ApprovalResultFrame,
  type ServerErrorCode,
  type ServerMessage,
  type SocketStatus,
  type StepFrame,
  type TurnTimings,
  type ClientMessage,
} from './ws';
import { deviceZone } from './format';
import { Outbox, type DeliveryState } from './outbox';
import { eventsToMessages, messageFromEvent } from './messages';

export interface ChatMessage {
  id: string;
  role: 'user' | 'agent';
  text: string;
  at: string;
  reactions: string[];
  /** Voice notes, images, documents and fetched links of this message. */
  attachments: Attachment[];
  /** Object URLs for attachments still uploading or just sent (previews before the server copy). */
  localUrls?: Record<string, string> | undefined;
  pending?: boolean;
  /** M5.1: sending until the server acknowledges; failed after 10 s without one (Retry). */
  delivery?: DeliveryState | undefined;
  trace_id?: string | undefined;
  /** Inline critical status shown under a user message when its turn (or an attachment) failed. */
  error?: string | undefined;
  /** Inline neutral note under a user message (a voice note in which nothing was said). */
  note?: string | undefined;
  /** Calendar answers (events or free slots) attached to TWO's bubble, as cards. */
  cards?: ScheduleCard[] | undefined;
}

/** A reminder, a monitor's match or the brief: TWO spoke first (M5). */
function isProactive(ev: Event): boolean {
  const raw = ev.raw_payload as { proactive?: unknown } | undefined;
  return Boolean(raw?.proactive);
}

/** The approval card persisted on TWO's bubble (M4.6), when the turn produced one. */
export function approvalOf(ev: Event): ApprovalFrame | undefined {
  const raw = ev.raw_payload as { approval?: unknown } | undefined;
  const a = raw?.approval as Partial<ApprovalFrame> | undefined;
  if (!a || typeof a.approval_id !== 'string' || typeof a.task_id !== 'string') return undefined;
  return { ...(a as ApprovalFrame), type: 'approval' };
}

export interface IntentInfo {
  event_id: string;
  intent: string;
  confidence: number;
  trace_id: string;
}

/** LiveMark states: the agent as a second self, not a chat status. */
export type LiveState = 'idle' | 'thinking' | 'working' | 'waiting' | 'done' | 'offline';

/** An attachment being sent with a message (already uploaded, with its preview). */
export interface OutgoingAttachment {
  attachment: Attachment;
  localUrl?: string | undefined;
}

interface AgentStore {
  status: SocketStatus;
  /** Global agent state for the header and rail. */
  liveState: LiveState;
  messages: ChatMessage[];
  draft: { trace_id: string; text: string } | null;
  turnState: AgentTurnState | null;
  turnLabel: string | null;
  currentTrace: string | null;
  /** Real steps of the current turn (transcribing, reading an image / document / link). */
  steps: StepFrame[];
  approvals: ApprovalFrame[];
  /** Live schedule cards of this session (a reload reads them from the bubbles instead). */
  schedules: ScheduleFrame[];
  /** "Reconnect Google" affordances after a provider refusal, per trace. */
  connectionActions: ConnectionActionFrame[];
  /** Proactive messages that arrived while the person was elsewhere (the header dot). */
  unseen: number;
  clearUnseen: () => void;
  /** Approval cards still waiting for the person (the header dot follows them too). */
  pendingApprovals: number;
  /** Outcomes per approval_id (approved with its checklist, cancelled, expired, failed). */
  approvalResults: Record<string, ApprovalResultFrame>;
  intents: IntentInfo[];
  hasMore: boolean;
  loadingHistory: boolean;
  /** Server timings of the last completed turn (debug panel). */
  lastTimings: TurnTimings | null;
  send: (text: string, attachments?: OutgoingAttachment[]) => void;
  /** M5.1: send a message that was not delivered again. */
  retry: (clientId: string) => void;
  loadOlder: () => Promise<void>;
}

const AgentContext = createContext<AgentStore | undefined>(undefined);

const PAGE = 50;
const DONE_VISIBLE_MS = 1500;

/** Error codes that are about an attachment; mapped to honest inline text by the UI language. */
const ATTACHMENT_ERROR_KEYS: Partial<Record<ServerErrorCode, string>> = {
  transcription_failed: 'two.errors.transcription_failed',
  image_failed: 'two.errors.image_failed',
  document_failed: 'two.errors.document_failed',
  link_failed: 'two.errors.link_failed',
  limit_reached: 'two.errors.limit_reached',
  attachment_not_found: 'two.errors.attachment_not_found',
  empty_transcript: 'two.errors.empty_transcript',
};
/** Codes rendered as a neutral note rather than a critical status. */
const NOTE_CODES = new Set<ServerErrorCode>(['empty_transcript']);

function mergeAttachments(existing: Attachment[], incoming: Attachment[]): Attachment[] {
  const out = [...existing];
  for (const a of incoming) {
    const idx = out.findIndex((x) => x.attachment_id === a.attachment_id);
    if (idx === -1) out.push(a);
    else out[idx] = { ...out[idx], ...a };
  }
  return out;
}

/**
 * Holds the live connection to the person's TWO and the conversation state, app-wide, so the
 * header and rail can show the agent's state on every destination.
 */
export function AgentProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState<{ trace_id: string; text: string } | null>(null);
  const [turnState, setTurnState] = useState<AgentTurnState | null>(null);
  /** Live action label from the state / step frames ("Transcribing…"); null when none. */
  const [turnLabel, setTurnLabel] = useState<string | null>(null);
  const [currentTrace, setCurrentTrace] = useState<string | null>(null);
  const [steps, setSteps] = useState<StepFrame[]>([]);
  const [approvals, setApprovals] = useState<ApprovalFrame[]>([]);
  const [schedules, setSchedules] = useState<ScheduleFrame[]>([]);
  const [connectionActions, setConnectionActions] = useState<ConnectionActionFrame[]>([]);
  const [unseen, setUnseen] = useState(0);
  const [approvalResults, setApprovalResults] = useState<Record<string, ApprovalResultFrame>>({});
  const [intents, setIntents] = useState<IntentInfo[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [lastTimings, setLastTimings] = useState<TurnTimings | null>(null);
  const doneTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  /**
   * Approval cards persisted on bubbles (M4.6): every card renders again on reload, in the
   * state the approvals table holds now (pending with buttons, approved with the checklist,
   * cancelled, expired, failed).
   */
  const absorbPersistedApprovals = useCallback((page: HistoryPage) => {
    const frames = page.events.flatMap((ev) => {
      const a = approvalOf(ev);
      return a ? [a] : [];
    });
    if (frames.length === 0) return;
    setApprovals((prev) => [
      ...frames.filter((f) => !prev.some((p) => p.approval_id === f.approval_id)),
      ...prev,
    ]);
    const states = page.approvals ?? {};
    setApprovalResults((prev) => {
      const next = { ...prev };
      for (const f of frames) {
        const s = states[f.approval_id];
        if (!s || s.status === 'pending' || next[f.approval_id]) continue;
        next[f.approval_id] = {
          type: 'approval_result',
          trace_id: f.trace_id,
          approval_id: f.approval_id,
          task_id: f.task_id,
          status: s.status === 'approved' ? 'approved' : s.status,
          checklist: s.checklist,
        };
      }
      return next;
    });
  }, []);

  // History once per sign-in; the prefetch started with the session probe (or right after
  // verify) is used when present, so the socket connect and the history load overlap.
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    (takePrefetchedHistory() ?? api.history({ limit: PAGE }))
      .then((res) => {
        if (cancelled) return;
        setMessages(eventsToMessages(res.events));
        setNextBefore(res.next_before);
        absorbPersistedApprovals(res);
      })
      .catch((err: unknown) => console.error('history failed', err))
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [me?.person_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Approvals still waiting after a reload: the tasks carry their card.
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    api
      .tasks()
      .then((res) => {
        if (cancelled) return;
        const pending = res.tasks.flatMap((task) =>
          task.approval && (task.state === 'waiting' || task.state === 'awaiting_approval')
            ? [task.approval]
            : [],
        );
        if (pending.length === 0) return;
        setApprovals((prev) => [
          ...prev.filter((a) => !pending.some((p) => p.approval_id === a.approval_id)),
          ...pending,
        ]);
        setTurnState((state) => state ?? 'waiting');
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [me?.person_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Proactive messages that waited since the last visit: the header dot.
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    api
      .homeSummary(readLastSeen())
      .then((s) => {
        if (!cancelled) setUnseen(s.unseen ?? 0);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [me?.person_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const clearUnseen = useCallback(() => setUnseen(0), []);

  // Remember the last visit when the tab goes away (feeds "While you were away").
  useEffect(() => {
    const mark = () => {
      if (document.visibilityState === 'hidden') writeLastSeen();
    };
    document.addEventListener('visibilitychange', mark);
    window.addEventListener('pagehide', () => writeLastSeen());
    return () => document.removeEventListener('visibilitychange', mark);
  }, []);

  useEffect(() => () => clearTimeout(doneTimer.current), []);

  // M5.1: every message goes through the outbox: queued, sent when a socket is open, delivered
  // on the server's acknowledgement, "not delivered" after 10 s without one, flushed on reconnect.
  const [outbox] = useState(
    () =>
      new Outbox<ClientMessage>({
        onChange: (entry) => {
          if (entry.state === 'delivered') return; // the ack handler updates the message itself
          setMessages((prev) =>
            prev.map((m) =>
              m.id === entry.client_id
                ? { ...m, delivery: entry.state, pending: entry.state !== 'delivered' }
                : m,
            ),
          );
        },
      }),
  );

  const onMessage = useCallback(
    (msg: ServerMessage) => {
      switch (msg.type) {
        case 'ack':
          // M5.1: delivered; the displayed time is the server's receipt time.
          outbox.acknowledge(msg.client_id);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === msg.client_id
                ? {
                    ...m,
                    id: msg.event.event_id,
                    pending: false,
                    delivery: 'delivered',
                    at: msg.event.created_at,
                    trace_id: msg.event.trace_id,
                    text: msg.event.text ?? m.text,
                    attachments: mergeAttachments(m.attachments, msg.event.attachments),
                  }
                : m,
            ),
          );
          return;
        case 'typing':
          clearTimeout(doneTimer.current);
          setCurrentTrace(msg.trace_id);
          setSteps([]);
          setDraft({ trace_id: msg.trace_id, text: '' });
          return;
        case 'state':
          clearTimeout(doneTimer.current);
          setCurrentTrace(msg.trace_id);
          setTurnState(msg.state);
          setTurnLabel(msg.label ?? null);
          // done → idle after 1.5 s; waiting persists until the person's next message (see send).
          if (msg.state === 'done') {
            doneTimer.current = setTimeout(() => setTurnState(null), DONE_VISIBLE_MS);
          }
          return;
        case 'step':
          setSteps((prev) => {
            const idx = prev.findIndex((s) => s.step_id === msg.step_id);
            if (idx === -1) return [...prev, msg];
            const next = prev.slice();
            next[idx] = msg;
            return next;
          });
          if (msg.status !== 'running') setTurnLabel(null);
          return;
        case 'delta':
          setDraft((d) =>
            d && d.trace_id === msg.trace_id
              ? { ...d, text: d.text + msg.text }
              : { trace_id: msg.trace_id, text: msg.text },
          );
          return;
        case 'message': {
          const ev = msg.event;
          if (
            isProactive(ev) &&
            (document.visibilityState === 'hidden' || !window.location.pathname.startsWith('/two'))
          ) {
            setUnseen((n) => n + 1);
          }
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.id === ev.event_id);
            if (idx !== -1) {
              // Same event again: it carries new attachments (a fetched link).
              const next = prev.slice();
              const cur = next[idx];
              if (cur) {
                next[idx] = {
                  ...cur,
                  attachments: mergeAttachments(cur.attachments, ev.attachments),
                };
              }
              return next;
            }
            if (!ev.text && ev.attachments.length === 0) return prev;
            // M5.2: the reply is persisted: the 👀 on the message it answers has done its job.
            const answered =
              ev.direction === 'outbound' && ev.reply_to
                ? prev.map((m) =>
                    m.id === ev.reply_to && m.reactions.includes('👀')
                      ? { ...m, reactions: m.reactions.filter((r) => r !== '👀') }
                      : m,
                  )
                : prev;
            return [...answered, messageFromEvent(ev)];
          });
          return;
        }
        case 'reaction':
          setMessages((prev) =>
            prev.map((m) =>
              m.id === msg.target_event_id && !m.reactions.includes(msg.emoji)
                ? { ...m, reactions: [...m.reactions, msg.emoji] }
                : m,
            ),
          );
          return;
        case 'schedule':
          setSchedules((prev) => [...prev.filter((c) => c.card_id !== msg.card_id), msg]);
          return;
        case 'connection_action':
          setConnectionActions((prev) => [...prev.filter((a) => a.trace_id !== msg.trace_id), msg]);
          return;
        case 'approval':
          setApprovals((prev) => [...prev.filter((a) => a.approval_id !== msg.approval_id), msg]);
          return;
        case 'approval_result':
          setApprovalResults((prev) => ({ ...prev, [msg.approval_id]: msg }));
          // The decision is made: waiting ends (done shows briefly, then idle).
          clearTimeout(doneTimer.current);
          setTurnState((state) => (state === 'waiting' ? 'done' : state));
          doneTimer.current = setTimeout(() => setTurnState(null), DONE_VISIBLE_MS);
          return;
        case 'done':
          setDraft(null);
          setTurnLabel(null);
          if (msg.timings) setLastTimings(msg.timings);
          return;
        case 'intent':
          setIntents((prev) =>
            [
              {
                event_id: msg.event_id,
                intent: msg.intent,
                confidence: msg.confidence,
                trace_id: msg.trace_id,
              },
              ...prev.filter((i) => i.event_id !== msg.event_id),
            ].slice(0, 20),
          );
          return;
        case 'error': {
          const key = ATTACHMENT_ERROR_KEYS[msg.code];
          const text = key ? t(key, { defaultValue: msg.message }) : t('two.errorInline');
          if (!key) {
            setTurnState(null);
            setDraft(null);
          }
          setMessages((prev) => {
            let idx = -1;
            if (msg.attachment_id) {
              idx = prev.findIndex((m) =>
                m.attachments.some((a) => a.attachment_id === msg.attachment_id),
              );
            }
            if (idx === -1) {
              for (let i = prev.length - 1; i >= 0; i--) {
                const m = prev[i];
                if (m?.role === 'user' && (!msg.trace_id || m.trace_id === msg.trace_id)) {
                  idx = i;
                  break;
                }
              }
            }
            if (idx === -1) return prev;
            const next = prev.slice();
            const target = next[idx];
            if (target) {
              next[idx] = NOTE_CODES.has(msg.code)
                ? { ...target, note: text }
                : { ...target, error: text };
            }
            return next;
          });
          return;
        }
        default:
          return;
      }
    },
    [t, outbox],
  );

  const onOpen = useCallback(() => {
    outbox.flush();
  }, [outbox]);
  const { status, send: sendFrame } = useAgentSocket(Boolean(me), onMessage, onOpen);
  useEffect(() => {
    outbox.setTransmit(sendFrame);
  }, [outbox, sendFrame]);

  const send = useCallback(
    (text: string, outgoing: OutgoingAttachment[] = []) => {
      const clientId = crypto.randomUUID();
      // A new message ends any waiting/done state and clears the previous turn's steps.
      clearTimeout(doneTimer.current);
      setTurnState(null);
      setTurnLabel(null);
      setSteps([]);
      const localUrls: Record<string, string> = {};
      for (const o of outgoing) if (o.localUrl) localUrls[o.attachment.attachment_id] = o.localUrl;
      setMessages((prev) => [
        ...prev,
        {
          id: clientId,
          role: 'user',
          text,
          at: new Date().toISOString(),
          reactions: [],
          attachments: outgoing.map((o) => o.attachment),
          ...(Object.keys(localUrls).length ? { localUrls } : {}),
          pending: true,
          delivery: 'sending',
        },
      ]);
      outbox.enqueue(clientId, {
        type: 'message',
        client_id: clientId,
        text,
        language: i18n.language,
        timezone: deviceZone(),
        ...(outgoing.length
          ? { attachment_ids: outgoing.map((o) => o.attachment.attachment_id) }
          : {}),
      });
    },
    [i18n.language, outbox],
  );

  const retry = useCallback(
    (clientId: string) => {
      outbox.retry(clientId);
    },
    [outbox],
  );

  const loadOlder = useCallback(async () => {
    if (!nextBefore) return;
    const res = await api.history({ before: nextBefore, limit: PAGE });
    setMessages((prev) => {
      const older = eventsToMessages(res.events);
      const seen = new Set(prev.map((m) => m.id));
      return [...older.filter((m) => !seen.has(m.id)), ...prev];
    });
    setNextBefore(res.next_before);
    absorbPersistedApprovals(res);
  }, [nextBefore, absorbPersistedApprovals]);

  // Cards without a decision yet: persisted expired ones already carry a result from the page.
  const pendingApprovals = useMemo(
    () => approvals.filter((a) => !approvalResults[a.approval_id]).length,
    [approvals, approvalResults],
  );

  const liveState: LiveState = status !== 'open' ? 'offline' : (turnState ?? 'idle');

  const value = useMemo<AgentStore>(
    () => ({
      status,
      liveState,
      messages,
      draft,
      turnState,
      turnLabel,
      currentTrace,
      steps,
      approvals,
      schedules,
      connectionActions,
      unseen,
      clearUnseen,
      pendingApprovals,
      approvalResults,
      intents,
      hasMore: Boolean(nextBefore),
      loadingHistory,
      lastTimings,
      send,
      retry,
      loadOlder,
    }),
    [
      status,
      liveState,
      messages,
      draft,
      turnState,
      turnLabel,
      currentTrace,
      steps,
      approvals,
      schedules,
      connectionActions,
      unseen,
      clearUnseen,
      pendingApprovals,
      approvalResults,
      intents,
      nextBefore,
      loadingHistory,
      lastTimings,
      send,
      retry,
      loadOlder,
    ],
  );

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
}

export function useAgent(): AgentStore {
  const ctx = useContext(AgentContext);
  if (!ctx) throw new Error('useAgent outside AgentProvider');
  return ctx;
}
